/* ═══════════════════════════════════════════════════════════════
   THE LADDER — the pure helpers behind the review wizard's five
   stage words, the driver page's Warnings card and the People
   list's position word (design 2026-09-11 ladder-phase3 §2, §6).

   One recommender on the server names the stage for every finding
   (Note, Internal discussion, First written warning, Final written
   warning, Hearing) from the operator's policy, the driver's score
   and the warnings that are live; a recorded stage becomes a
   warning with a life. These helpers only put the words around
   what the server sent: no React, no fetch, no Date.now() unless
   a `now` is passed. '' / [] on rubbish, never a throw.
   ═══════════════════════════════════════════════════════════════ */

import { fmtDateWords, todayIsoLocal } from './today.js';

const isObj = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);

/* ── the five stages ────────────────────────────────────────── */

/**
 * The ladder, bottom to top. `tier` is the existing escalation tier
 * stored alongside each stage (design §2) — the letter wording and
 * every stored row keep working on it.
 */
export const STAGES = Object.freeze([
  Object.freeze({ stage: 'note', word: 'Note', tier: 'REVIEW_SIGN' }),
  Object.freeze({ stage: 'discussion', word: 'Internal discussion', tier: 'IDD' }),
  Object.freeze({ stage: 'first_written', word: 'First written warning', tier: 'DISCIPLINARY' }),
  Object.freeze({ stage: 'final_written', word: 'Final written warning', tier: 'DISCIPLINARY' }),
  Object.freeze({ stage: 'hearing', word: 'Hearing', tier: 'DISCIPLINARY' }),
]);

const BY_STAGE = Object.fromEntries(STAGES.map((s) => [s.stage, s]));

function stageKey(stage) {
  return typeof stage === 'string' ? stage.trim().toLowerCase() : '';
}

/** The word on screen for a stage ('first_written' → 'First written warning'); ''. */
export function stageWord(stage) {
  const s = BY_STAGE[stageKey(stage)];
  return s ? s.word : '';
}

/** The escalation tier stored alongside a stage (design §2); ''. */
export function tierFor(stage) {
  const s = BY_STAGE[stageKey(stage)];
  return s ? s.tier : '';
}

/**
 * The lowest stage of a tier — for a recommendation or a row that still
 * speaks only in tiers (a server without phase 3). ''.
 */
export function stageForTier(tier) {
  const t = typeof tier === 'string' ? tier.trim().toUpperCase() : '';
  const s = STAGES.find((x) => x.tier === t);
  return s ? s.stage : '';
}

/* ── the reasons ────────────────────────────────────────────── */

/**
 * The recommendation's reasons as lines (`reasons: [strings]`, design
 * §3); the single `reason` when there is no list. Blank lines dropped;
 * [] on rubbish.
 */
export function reasonLines(rec) {
  if (!isObj(rec)) return [];
  const list = Array.isArray(rec.reasons)
    ? rec.reasons.filter((r) => typeof r === 'string' && r.trim()).map((r) => r.trim())
    : [];
  if (list.length > 0) return list;
  const one = typeof rec.reason === 'string' ? rec.reason.trim() : '';
  return one ? [one] : [];
}

/* ── dates ──────────────────────────────────────────────────── */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/* The ISO day a timestamp or date string names; '' on rubbish. */
function isoDay(v) {
  if (typeof v !== 'string') return '';
  const m = v.trim().match(ISO_DAY);
  return m && fmtDateWords(m[0]) ? m[0] : '';
}

/* '3 Mar', with the year when the date is not in `now`'s year (or when
   no clock is given) — the same rule as the score card's fade line. */
function dateWords(iso, now) {
  const day = isoDay(iso);
  if (!day) return '';
  const year = Number(day.slice(0, 4));
  const thisYear = now instanceof Date && !isNaN(now.getTime()) && now.getFullYear() === year;
  return `${fmtDateWords(day)}${thisYear ? '' : ` ${year}`}`;
}

/* Today as an ISO day when a clock is given; '' otherwise. */
function todayOf(now) {
  return now instanceof Date && !isNaN(now.getTime()) ? todayIsoLocal(now) : '';
}

/* Live: the stored status says so and the life has not run out
   (`lives_until` null or ≥ today; design §3). Without a clock the
   stored status is taken as read. */
function isLive(status, livesUntil, now) {
  if (status !== 'live') return false;
  const until = isoDay(livesUntil);
  if (!until) return true;
  const today = todayOf(now);
  return !today || until >= today;
}

/* The word the server sent for a stage, else the lookup; ''. */
function wordOf(stage, sent) {
  const word = typeof sent === 'string' ? sent.trim() : '';
  return word || stageWord(stage);
}

/**
 * The person behind an audit stamp, in words. The ledger stores who did
 * it as the manager's login (an email); a sentence says "by Jo Bloggs",
 * never "by jo.bloggs@example.com". A stamp that is not an email is
 * shown as it is; '' on rubbish.
 */
export function personWord(stamp) {
  const s = typeof stamp === 'string' ? stamp.trim() : '';
  if (!s) return '';
  if (!s.includes('@')) return s;
  const local = s.split('@', 1)[0];
  const words = local.split(/[._\-+]+/).filter(Boolean);
  if (!words.length) return s;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/* ── the position line ──────────────────────────────────────── */

/**
 * "Position: first written warning, live until 3 Mar" from a
 * `position: {stage, stage_word, lives_until} | null`; a hearing
 * carries no date ("open until an outcome is recorded"); otherwise
 * "No live warnings". A position at `note` is no warning at all.
 */
export function positionLine(position, now) {
  const none = 'No live warnings';
  if (!isObj(position)) return none;
  const stage = stageKey(position.stage);
  if (!BY_STAGE[stage] || stage === 'note') return none;
  if (!isLive('live', position.lives_until, now)) return none;
  const word = wordOf(stage, position.stage_word).toLowerCase();
  const until = dateWords(position.lives_until, now);
  if (until) return `Position: ${word}, live until ${until}`;
  if (stage === 'hearing') return `Position: ${word}, open until an outcome is recorded`;
  return `Position: ${word}`;
}

/* ── the warning rows ───────────────────────────────────────── */

function outcomeText(outcome, at, now) {
  const raw = typeof outcome === 'string' ? outcome.trim() : '';
  if (!raw) return '';
  const word = raw === 'RESOLVED' ? 'resolved' : raw === 'ESCALATE' ? 'escalate' : raw;
  const when = dateWords(at, now);
  return `Outcome: ${word}${when ? `, ${when}` : ''}`;
}

/**
 * The driver's warnings as rows, newest issued first:
 *   { id, stage, word, issued: '3 Sep', by: 'Zed',
 *     life: 'lives until 3 Mar' | 'lapsed 3 Mar' | 'withdrawn'
 *         | 'open until an outcome is recorded',
 *     outcome: 'Outcome: resolved, 8 Sep' | '', status, live: bool }
 * A live warning whose date has passed reads lapsed (lapsed is a
 * fact about the date, design §4). Rows without a stage are dropped;
 * [] on rubbish.
 */
export function warningRows(warnings, now) {
  if (!Array.isArray(warnings)) return [];
  return warnings
    .filter((w) => isObj(w) && BY_STAGE[stageKey(w.stage)])
    .map((w) => {
      const stage = stageKey(w.stage);
      const status = typeof w.status === 'string' ? w.status.trim().toLowerCase() : '';
      const live = isLive(status, w.lives_until, now);
      const until = dateWords(w.lives_until, now);
      let life;
      if (status === 'withdrawn') life = 'withdrawn';
      else if (live) life = until ? `lives until ${until}` : stage === 'hearing' ? 'open until an outcome is recorded' : 'live';
      else life = until ? `lapsed ${until}` : 'lapsed';
      return {
        id: w.id,
        stage,
        word: wordOf(stage, w.stage_word),
        issued: dateWords(w.issued_at, now),
        issuedIso: isoDay(w.issued_at),
        by: personWord(w.issued_by),
        life,
        outcome: outcomeText(w.outcome, w.outcome_at, now),
        status: live ? 'live' : status === 'withdrawn' ? 'withdrawn' : 'lapsed',
        live,
        // The finding the warning came from — its note is what Print opens
        // for signature on paper (phase 4). null when there is none.
        sourceInfringementId: w.source_infringement_id ?? null,
        // The day the warning's letter was issued (phase 5): '12 Sep' when
        // the server sends `letter_issued_at`, '' when it does not or the
        // letter is still to be drafted.
        letterIssued: dateWords(w.letter_issued_at, now),
        letterIssuedIso: isoDay(w.letter_issued_at),
      };
    })
    .sort((a, b) => b.issuedIso.localeCompare(a.issuedIso));
}

/**
 * One row as the design's sentence: "First written warning · issued
 * 3 Sep by Zed · lives until 3 Mar". Parts that are blank are left
 * out; '' on rubbish.
 */
export function warningLine(row) {
  if (!isObj(row) || !row.word) return '';
  const issued = row.issued ? `issued ${row.issued}${row.by ? ` by ${row.by}` : ''}` : '';
  return [row.word, issued, row.life].filter(Boolean).join(' · ');
}

/* ── the People list ────────────────────────────────────────── */

/**
 * The position word after the score on the People list (a GET
 * /api/today drivers[] row with `position: {stage, stage_word,
 * lives_until} | null`): 'first written warning' while a warning is
 * live, '' otherwise. Lowercased: it is a fact in a sentence.
 */
export function listPositionText(row, now) {
  if (!isObj(row) || !isObj(row.position)) return '';
  const stage = stageKey(row.position.stage);
  if (!BY_STAGE[stage] || stage === 'note') return '';
  if (!isLive('live', row.position.lives_until, now)) return '';
  return wordOf(stage, row.position.stage_word).toLowerCase();
}

/* ── the Infringements table ────────────────────────────────── */

/**
 * The stage word for a row that carries a stored `stage`; '' so the
 * caller can fall back to the tier label a row without one still has.
 */
export function routeWord(row) {
  if (!isObj(row)) return '';
  return stageWord(row.stage);
}
